import type { DedupCandidate, DedupSourceInfo } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { GitMerge, X, FileText, Quote } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CandidateCardProps {
  candidate: DedupCandidate
  onMerge: () => void
  onDismiss: () => void
}

export function CandidateCard({ candidate, onMerge, onDismiss }: CandidateCardProps) {
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="grid grid-cols-2 gap-4 h-full">
        {/* Entity A */}
        <EntityPanel
          label={candidate.entityALabel}
          description={candidate.entityADescription}
          confidence={candidate.entityAConfidence}
          source={candidate.entityASource}
          status={candidate.entityAStatus}
          sources={candidate.entityASources}
          evidence={candidate.entityAEvidence}
          isCanonical={candidate.entityAConfidence >= candidate.entityBConfidence}
        />

        {/* Entity B */}
        <EntityPanel
          label={candidate.entityBLabel}
          description={candidate.entityBDescription}
          confidence={candidate.entityBConfidence}
          source={candidate.entityBSource}
          status={candidate.entityBStatus}
          sources={candidate.entityBSources}
          evidence={candidate.entityBEvidence}
          isCanonical={candidate.entityBConfidence > candidate.entityAConfidence}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-border-subtle">
        <Button
          variant="ghost"
          size="sm"
          className="text-text-muted hover:text-text gap-1"
          onClick={onDismiss}
        >
          <X className="w-3.5 h-3.5" />
          Keep Separate
        </Button>
        <Button
          size="sm"
          className="bg-market hover:bg-market-dark text-white gap-1"
          onClick={onMerge}
        >
          <GitMerge className="w-3.5 h-3.5" />
          Merge
        </Button>
      </div>
    </div>
  )
}

function EntityPanel({
  label,
  description,
  confidence,
  source,
  status,
  sources,
  evidence,
  isCanonical,
}: {
  label: string
  description: string
  confidence: number
  source: string
  status: string
  sources: DedupSourceInfo[]
  evidence: string[]
  isCanonical: boolean
}) {
  const confidencePercent = Math.round(confidence * 100)

  return (
    <div className={cn(
      'rounded-lg border p-3 flex flex-col gap-2 overflow-y-auto',
      isCanonical ? 'border-market/30 bg-market/5' : 'border-border-subtle bg-warm-50'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-text truncate">{label}</h4>
        {isCanonical && (
          <Badge className="bg-market/10 text-market border-market/20 text-[10px] flex-shrink-0">Canonical</Badge>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">{description}</p>
      )}

      {/* Metadata row */}
      <div className="flex items-center gap-3 text-xs text-text-muted">
        <span>
          Confidence: <span className={cn('font-medium', confidencePercent >= 70 ? 'text-market' : 'text-solution')}>{confidencePercent}%</span>
        </span>
        <span>
          Origin: <span className="font-medium text-text capitalize">{source}</span>
        </span>
        {status !== 'pending' && (
          <Badge variant="outline" className="text-[10px] capitalize">{status}</Badge>
        )}
      </div>

      {/* Sources */}
      {sources.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">Sources</span>
          {sources.map((src, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <FileText className="w-3 h-3 text-text-muted flex-shrink-0" />
              <span className="text-text-secondary truncate" title={src.uri}>{src.title}</span>
            </div>
          ))}
        </div>
      )}

      {/* Evidence */}
      {evidence.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-[10px] font-medium text-text-muted uppercase tracking-wider">Evidence</span>
          {evidence.map((quote, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs">
              <Quote className="w-3 h-3 mt-0.5 flex-shrink-0 text-text-muted" />
              <span className="text-text-secondary line-clamp-2 italic">&ldquo;{quote}&rdquo;</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
